import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { ActivationEnd, Router } from '@angular/router';
import {
    ConfigProvider,
    DraftModeProvider,
    FLOORI_CONFIG_PROVIDER,
    FLOORI_NAVIGATION,
    LocalStorageKeys,
    NavigationProvider,
    StorageProvider,
} from '@floori-web/models';
import {
    FlooriDialogProvider,
    FLOORI_DIALOG_SERVICE,
    FlooriDialogRef,
    SnackbarDialogData,
    flooriHelperClasses,
    FLOORI_STORAGE_PROVIDER,
    FLOORI_TRANSLATOR,
    TranslationsProvider,
    ResponseTypes,
    SnackbarPosition,
} from '@floori-web-components';
import {
    filter,
    switchMap,
    of,
    tap,
    BehaviorSubject,
    distinctUntilChanged,
    EMPTY,
    from,
    Observable,
    combineLatest,
    take,
} from 'rxjs';
import { ConfigState } from '@floori/models';

@Injectable()
export class DraftModeService implements DraftModeProvider {
    private renderer: Renderer2;
    private draft: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private draftPopupRef?: FlooriDialogRef;
    isDraftMode$ = this.draft.asObservable();

    constructor(
        private readonly router: Router,
        private readonly rendererFactory: RendererFactory2,
        @Inject(FLOORI_STORAGE_PROVIDER) private readonly storageProvider: StorageProvider,
        @Inject(FLOORI_DIALOG_SERVICE) private readonly dialogProvider: FlooriDialogProvider,
        @Inject(FLOORI_TRANSLATOR) private readonly translationsProvider: TranslationsProvider,
        @Inject(FLOORI_NAVIGATION) private readonly navigationProvider: NavigationProvider,
        @Inject(FLOORI_CONFIG_PROVIDER) private readonly configProvider: ConfigProvider,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    init(): void {
        this.setDraftMode();
        this.watchDraftMode();
    }

    toggleDraftMode(): void {
        this.configProvider.companyConfig$
            .pipe(
                take(1),
                filter(config => this.isPreviewModeEnabled(config)),
                tap(() => {
                    const newValue = !this.draft.getValue();
                    this.draft.next(newValue);
                    this.navigationProvider.appendQueryParam('draft', newValue ? 'true' : null);
                }),
            )
            .subscribe();
    }

    private setDraftMode(): void {
        combineLatest([this.router.events, this.configProvider.companyConfig$])
            .pipe(
                filter(([e]) => e instanceof ActivationEnd),
                tap(([e, config]) => {
                    const hasDraftParam =
                        (e as ActivationEnd).snapshot.queryParamMap.get('draft') === 'true';
                    this.draft.next(this.isPreviewModeEnabled(config) && hasDraftParam);
                }),
            )
            .subscribe();
    }

    private watchDraftMode(): void {
        this.draft
            .asObservable()
            .pipe(
                distinctUntilChanged(),
                tap(draft => {
                    if (draft) {
                        this.enableDraft();
                        return;
                    }
                    this.disableDraft();
                }),
            )
            .subscribe();
    }

    private disableDraft(): void {
        this.renderer.removeClass(document.body, 'draft');
        this.draftPopupRef?.close();
    }

    private enableDraft(): void {
        this.renderer.addClass(document.body, 'draft');
        this.storageProvider
            .get<boolean>(LocalStorageKeys.draftPopupClosed)
            .pipe(
                filter(closed => !closed),
                switchMap(() => this.openDraftModePopup()),
                switchMap(closed =>
                    closed
                        ? this.storageProvider.set<boolean>(LocalStorageKeys.draftPopupClosed, true)
                        : of(undefined),
                ),
            )
            .subscribe();
    }

    private openDraftModePopup(): Observable<unknown> {
        return from(import('@floori-web-components')).pipe(
            // This is component name so cannot apply camelCase to it
            // eslint-disable-next-line @typescript-eslint/naming-convention
            switchMap(({ FlooriSnackbarComponent: FlooriSnackbarComponent }) => {
                if (this.draftPopupRef) {
                    return EMPTY;
                }

                this.draftPopupRef = this.dialogProvider.open<unknown, SnackbarDialogData>(
                    FlooriSnackbarComponent,
                    {
                        containerClass: [
                            flooriHelperClasses['snackbar'],
                            flooriHelperClasses['stretchWrapper'],
                        ],
                        panelClass: flooriHelperClasses['transparentOverlay'],
                        hasBackdrop: false,
                        closeOnDestroy: true,
                        data: {
                            text: this.translationsProvider.translate('draftMode.snackbarLabel'),
                            position: SnackbarPosition.top,
                            showCloseButton: true,
                            type: ResponseTypes.error,
                        },
                    },
                ) as FlooriDialogRef;

                return this.draftPopupRef?.beforeClosed();
            }),
        );
    }

    private isPreviewModeEnabled(config: ConfigState | null | undefined): boolean {
        return config?.company?.isPreviewModeEnabled !== false;
    }
}
